import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LogCategory, LogService, LogType, LoggerService } from '../core/services/logger.service';


@Injectable({
    providedIn: 'root'
})
export class SolarWindsService {

    private solarWindsApiUrl: string = environment.apiUrl + '/solarwinds';
    public isFetchingMyRequests: boolean = false;

    constructor(
        private http: HttpClient,
        private logger: LoggerService
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.MySupportRequests, category);
    }

    //  myRequests$ needs to refresh the list once a cancel submission is completed.  This behavior subject helps facilitate that.
    private _listRequests$: BehaviorSubject<void> = new BehaviorSubject(undefined);

    public myRequests$ = this._listRequests$.pipe(
        switchMap(() => {
            this.isFetchingMyRequests = true;

            return this.http.get<any>(`${this.solarWindsApiUrl}/self/incidents`).pipe(
                tap(() => {
                    this.log('myRequests$', 'Fetched requests for current user', LogCategory.SubscriptionData);
                    this.isFetchingMyRequests = false;
                }),
                shareReplay(1)
            )
        })
    );

    private _equipment$: Observable<any>
    public get equipment$(): Observable<any> {
        if (!this._equipment$) {
            this._equipment$ = this.http.get<any>(this.solarWindsApiUrl + '/self/equipment').pipe(
                tap(() => this.log('equipment$', 'Fetched equipment for current user', LogCategory.SubscriptionData)),
                map((equip:any) => {
                    let equipment = this.formatEquipment(equip);
                    return equipment;
                }),
                shareReplay(1)
            )
        }
        return this._equipment$;
    }

    public updateRequestStatusToCancelled(incidentId) {
        const url = this.solarWindsApiUrl + '/cancel';
        this.log("updatePagePreference", url);

        return this.http.put<any>(url, incidentId)

    }

    public refresh() {
        this._listRequests$.next();
    }

    // Used to create a standard format for any SW equipment returned.
    public formatEquipment(equipment: any) {
        let personal = equipment.Personal;
        let position = equipment.Position;

        let computers = [];
        if (personal?.Computers)
            computers = computers.concat(personal.Computers);

        if (position?.Computers)
            computers = computers.concat(position.Computers);

        let assets = [];
        if (personal?.Assets)
            assets = assets.concat(personal.Assets);

        if (position?.Assets)
            assets = assets.concat(position.Assets);

        let departmental: any = {};
        departmental.Computers = this.groupEquipment(computers, "yes");
        departmental.Assets = this.groupEquipment(assets, "yes");

        if ((departmental.Computers == null) && (departmental.Assets == null))
            departmental = null;

        let assigned: any = {};
        assigned.Computers = this.groupEquipment(computers, "no");
        assigned.Assets = this.groupEquipment(assets, "no");

        if ((assigned.Computers == null) && (assigned.Assets == null))
            assigned = null;

        return {
            departmental: departmental,
            assigned: assigned
        }
    }

    private groupEquipment(group: any[], isDepartmental: string) {
        if (!group.length)
            return null;

        // First get just the items that match the departmental association;
        group = group.filter(g => g.DepartmentalAsset?.toLowerCase() == isDepartmental);

        if (!group.length)
            return null;

        //  Then if we have any items, group them by Type
        return group.reduce((acc, item) => {
            (acc[item.Type] = acc[item.Type] || []).push(item);
            return acc;
        }, {});
    }

    //  Creates a new purchase ticket
    public purchase$(data: any): Observable<any> {
        return this.http.post<any>(`${this.solarWindsApiUrl}/incidents/purchases`, data).pipe(
            tap(() => this.refresh()),
            catchError((error,) => {
                return of(error);
            }),
        );
    }

    public createRecording$(data: any): Observable<any> {
        return this.http.post<any>(`${this.solarWindsApiUrl}/incidents/recordings`, data).pipe(
            tap(() => this.refresh()),
            catchError((error,) => {
                return of(error);
            }),
        );
    }

    public updateRecording$(data: any): Observable<any> {
        return this.http.put<any>(`${this.solarWindsApiUrl}/incidents/recordings`, data).pipe(
            tap(() => this.refresh()),
            catchError((error,) => {
                return of(error);
            }),
        );
    }
}
